import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 10000, // 10 second timeout to prevent hanging
});

// Mirror the global axios cross-origin auth interceptors (defined in
// /app/frontend/src/index.js). The `apiClient` instance was historically
// the only path with refresh logic; with the 2026-05-24 production
// auth-persistence fix, we need to keep them in lockstep so apiClient
// calls also benefit from the localStorage Bearer fallback when
// cross-origin cookies are evicted.
apiClient.interceptors.request.use((config) => {
  try {
    if (typeof window !== 'undefined') {
      const access = window.__hlAuth?.getAccess?.();
      if (access) {
        config.headers = config.headers || {};
        if (!config.headers.Authorization && !config.headers.authorization) {
          config.headers.Authorization = `Bearer ${access}`;
        }
      }
    }
  } catch (_) { /* never block request on interceptor error */ }
  return config;
});

// ---------------------------------------------------------------------------
// Silent refresh — single-flight lock
//
// When the short-lived access_token expires the browser still holds a valid
// 30-day refresh_token (cookie OR localStorage fallback). We swap it
// transparently for a new access token and replay the original request,
// so the user never sees a forced re-login during a normal session. Only
// when the refresh itself fails (truly expired, revoked, or absent) do we
// surface the session-expired flow.
// ---------------------------------------------------------------------------
let refreshInFlight = null;

const performRefresh = () => {
  if (!refreshInFlight) {
    // Send the refresh_token via Bearer header as the cross-origin
    // fallback (cookies may be evicted in production). Backend accepts
    // either source.
    const headers = {};
    if (typeof window !== 'undefined') {
      const lsRefresh = window.__hlAuth?.getRefresh?.();
      if (lsRefresh) headers.Authorization = `Bearer ${lsRefresh}`;
    }
    refreshInFlight = axios
      .post(`${API}/auth/refresh`, {}, { withCredentials: true, headers })
      .then((res) => {
        // Persist newly-issued tokens immediately so subsequent calls
        // pick them up via the REQUEST interceptor.
        if (res?.data?.tokens && typeof window !== 'undefined') {
          window.__hlAuth?.storeTokens?.(res.data.tokens);
        }
        return res;
      })
      .finally(() => {
        // Release the lock on next tick so concurrent 401s can queue on the
        // same promise above, but later 401s start a fresh attempt.
        setTimeout(() => { refreshInFlight = null; }, 0);
      });
  }
  return refreshInFlight;
};

// Add response interceptor to handle 401 errors globally
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle network errors (offline)
    if (!error.response && error.message === 'Network Error') {
      console.log('Network error detected - likely offline');
      // Don't redirect or show error toast for offline state
      return Promise.reject(error);
    }

    const original = error.config || {};
    const url = original.url || '';

    // Never try to refresh on the refresh endpoint itself (would loop) or on
    // login/register (a 401 there is a credential failure, not a session
    // expiry).
    const isAuthExempt =
      url.includes('/auth/refresh') ||
      url.includes('/auth/login') ||
      url.includes('/auth/register');

    if (error.response?.status === 401 && !isAuthExempt && !original._retried) {
      original._retried = true;
      try {
        await performRefresh();
        // Retry the original request with the new cookie set.
        return apiClient(original);
      } catch (refreshErr) {
        // Refresh genuinely failed — fall through to the logout path below.
      }
    }

    if (error.response?.status === 401) {
      // Don't show toast for /auth/me endpoint (it's expected on initial load)
      if (!url.includes('/auth/me')) {
        toast.error('Your session has expired. Please sign in again.');
        // Flag for the Login page to render an inline session-expired banner.
        try { sessionStorage.setItem('healthloop_session_expired', '1'); } catch (_) { /* storage disabled */ }
        // Clear stale localStorage tokens — refresh truly failed.
        if (typeof window !== 'undefined') {
          window.__hlAuth?.clearTokens?.();
        }
        // Redirect to login after a short delay
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      }
    }
    return Promise.reject(error);
  }
);

export const healthApi = {
  // Daily Check-ins
  createCheckIn: (data) => apiClient.post('/checkins', data),
  getCheckIns: (limit = 30) => apiClient.get(`/checkins?limit=${limit}`),

  // Habit Tracking
  trackHabit: (data) => apiClient.post('/habits', data),
  getHabits: (limit = 30) => apiClient.get(`/habits?limit=${limit}`),

  // Medications
  createMedication: (data) => apiClient.post('/medications', data),
  getMedications: () => apiClient.get('/medications'),
  deleteMedication: (id) => apiClient.delete(`/medications/${id}`),
  getRefillAlerts: () => apiClient.get('/medications/refill-alerts'),
  getMedicationIntelligence: () => apiClient.get('/medications/intelligence'),

  // AI Features
  generateInsights: () => apiClient.post('/ai/generate-insights'),
  getInsights: (limit = 20) => apiClient.get(`/insights?limit=${limit}`),
  generateRecommendations: () => apiClient.post('/ai/generate-recommendations'),
  getRecommendations: (limit = 20) => apiClient.get(`/recommendations?limit=${limit}`),
  generateMealPlan: (data) => apiClient.post('/ai/generate-meal-plan', data),
  getMealPlans: (limit = 10) => apiClient.get(`/meal-plans?limit=${limit}`),

  // Rule-Based Insights
  getRuleBasedInsights: () => apiClient.get('/insights/rule-based'),

  // Wellness Score
  getWellnessScore: () => apiClient.get('/wellness-score'),

  // Trends
  getTrendsData: (days = 7) => apiClient.get(`/trends?days=${days}`),
  getTrends: (days = 7) => apiClient.get(`/trends?days=${days}`), // Alias for backward compatibility
  getCheckInTrends: (days = 14) => apiClient.get(`/checkins/trends?days=${days}`),

  // Notifications
  getNotifications: () => apiClient.get('/notifications'),
  markNotificationRead: (id) => apiClient.put(`/notifications/${id}/read`),
  createTestNotification: () => apiClient.post('/notifications/test'),

  // Support
  // (Legacy `/support` endpoints removed during 2026-05-11 consolidation.
  //  All support flows now route through `/support-tickets` below.)
  
  // Admin Analytics
  getAnalyticsStats: (days = 30) => apiClient.get(`/analytics/stats?days=${days}`),
  getMealSwapAnalytics: (days = 30) => apiClient.get(`/analytics/meal-swaps?days=${days}`),
  
  // Support Tickets (Support & Help section)
  createSupportTicket: (data) => apiClient.post('/support-tickets', data),
  getUserSupportTickets: () => apiClient.get('/support-tickets'),

  // Specialist Requests
  createSpecialistRequest: (data) => apiClient.post('/specialist-requests', data),
  createSpecialistRequestWithFiles: (formData) => apiClient.post('/specialist-requests-with-files', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadSupportDocument: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/upload-support-document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getSpecialistRequests: () => apiClient.get('/specialist-requests'),

  // Dashboard
  getDashboardStats: () => apiClient.get('/dashboard/stats'),
  
  // Daily Health Insight
  getDailyHealthInsight: () => apiClient.get('/daily-health-insight'),
  
  // Daily Health Tracker (Engagement)
  getDailyTracker: async () => {
    try {
      const response = await apiClient.get('/daily-tracker/today');
      return response;
    } catch (error) {
      // If offline, try to return cached data structure
      if (!navigator.onLine) {
        console.log('Offline: Returning minimal tracker structure');
        return {
          data: {
            tracker: {
              id: 'offline',
              date: new Date().toISOString().split('T')[0],
              completion_percentage: 0,
              health_score: 0,
              streak_count: 0
            },
            tasks: [
              { id: 'water', name: 'Drink 8 glasses of water', emoji: '💧', completed: false },
              { id: 'meals', name: 'Eat 3 balanced meals', emoji: '🍽️', completed: false },
              { id: 'steps', name: 'Walk 10,000 steps', emoji: '👟', completed: false },
              { id: 'sleep', name: 'Get 7-8 hours sleep', emoji: '😴', completed: false }
            ],
            motivational_message: 'You are offline. Data will sync when reconnected.'
          }
        };
      }
      throw error;
    }
  },
  
  toggleDailyTask: async (taskId) => {
    try {
      const response = await apiClient.post('/daily-tracker/toggle-task', { task_id: taskId });
      return response;
    } catch (error) {
      // If offline, return success structure for optimistic updates
      if (!navigator.onLine) {
        console.log('Offline: Task toggle queued');
        return {
          data: {
            tracker: { completion_percentage: 0, health_score: 0 },
            tasks: [],
            motivational_message: 'Saved offline. Will sync when online.'
          }
        };
      }
      throw error;
    }
  },
  // Atomic batch — used by Smart Check-In submit so concurrent task writes
  // can't race each other in MongoDB.
  markTasksComplete: (taskIds) =>
    apiClient.post('/daily-tracker/mark-tasks-complete', { task_ids: taskIds }),

  // Soft "I'm not feeling it today" — records a rest day, doesn't break streak.
  recordRestDay: () => apiClient.post('/checkins/skip', {}),

  // Calm narrative description of recent consistency (no scoreboard).
  getStreakStory: () => apiClient.get('/streak/story'),
  updateTimezone: (tz) => apiClient.put('/auth/timezone', { timezone: tz }),
  updateNudgeOptIn: (optIn) => apiClient.put('/auth/nudge-opt-in', { opt_in: optIn }),

  getStreakStats: () => apiClient.get('/daily-tracker/streak'),
  getStreakStatus: () => apiClient.get('/streak/status'),
  getWeeklyDigest: () => apiClient.get('/weekly-digest'),
  
  // Smart Daily Check-In (rich signals: mood/stress/environment)
  createSmartCheckIn: (data) => apiClient.post('/checkins/smart', data),
  getSmartCheckInToday: () => apiClient.get('/checkins/smart/today'),

  // Computed Notifications (real-time, non-spammy)
  getComputedNotifications: () => apiClient.get('/notifications/computed'),
  
  // AI Health Insights
  getAIInsights: () => apiClient.get('/ai-insights/today'),
  
  // Onboarding
  completeOnboarding: (data) => apiClient.put('/auth/onboarding', data),
  
  // Subscriptions
  getSubscriptionStatus: () => apiClient.get('/subscriptions/status'),
  
  // Meal Swaps (Freemium Model)
  checkMealSwapLimit: () => apiClient.get('/meal-swaps/check'),
  trackMealSwap: () => apiClient.post('/meal-swaps/track'),
  
  // Community challenges
  getActiveChallenges: () => apiClient.get('/community/challenges/active'),
  getChallenge: (challengeId) => apiClient.get(`/community/challenges/${challengeId}`),
  joinChallenge: (challengeId, privacySetting) => 
    apiClient.post(`/community/challenges/${challengeId}/join`, { privacy_setting: privacySetting }),
  getChallengeProgress: (challengeId) => apiClient.get(`/community/challenges/${challengeId}/my-progress`),
  getLeaderboard: (challengeId, limit = 10) => 
    apiClient.get(`/community/leaderboard/${challengeId}`, { params: { limit } }),
  getMotivationalMessages: () => apiClient.get('/community/motivational-messages'),
  getCommunitySettings: () => apiClient.get('/community/settings'),
  updatePrivacySettings: (privacyLevel, showInLeaderboard) => 
    apiClient.put('/community/settings/privacy', { 
      privacy_level: privacyLevel, 
      show_in_leaderboard: showInLeaderboard 
    }),

  // Daily Health Loop - Habit Tracking with Streaks & Badges
  logDailyHabits: (habits) => apiClient.post('/daily-habits', { habits }),
  getTodayHabits: () => apiClient.get('/daily-habits/today'),
  getHabitHistory: (days = 7) => apiClient.get(`/daily-habits/history?days=${days}`),
  getUserStreaks: () => apiClient.get('/streaks'),
  getUserBadges: () => apiClient.get('/badges'),

  // Cultural Adaptation
  getAvailableRegions: () => apiClient.get('/culture/regions'),
  getRegionDetails: (regionKey) => apiClient.get(`/culture/region/${regionKey}`),
  updateCulturalSettings: (settings) => apiClient.put('/culture/settings', settings),
  getCulturalSettings: () => apiClient.get('/culture/settings'),
  getCulturalExamples: () => apiClient.get('/culture/examples'),

  // Wellness tip engagement (lightweight telemetry, fire-and-forget)
  recordTipEngagement: (action, category, tipText) =>
    apiClient.post('/wellness/tip-engagement', {
      action,
      category,
      tip_text: tipText,
    }),
};

export default healthApi;
